import React, { ReactElement, useSyncExternalStore } from 'react';
import { isMobile } from 'react-device-detect';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import { useHasMounted } from '~/components/ClientOnly';
import { hmrSafeContext } from '~/utils/hmrSafeContext';

export const FullscreenContext = hmrSafeContext('__turfmaster_fullscreen_ctx__', {
  isFullscreen: false,
  setIsFullscreen: (_fullscreen: boolean) => {},
  toggleIsFullscreen: () => {},
  dontAskAgain: false,
  setDontAskAgain: (_dontAskAgain: boolean) => {},
});

const fullscreenDontAskAgain = 'fullscreenDontAskAgain';

const fullscreenDontAskListeners = new Set<() => void>();

function handleFullscreenDontAskStorage(event: StorageEvent): void {
  if (event.key === fullscreenDontAskAgain) {
    fullscreenDontAskListeners.forEach((l) => l());
  }
}

function subscribeDontAskAgain(onStoreChange: () => void): () => void {
  const hadListeners = fullscreenDontAskListeners.size > 0;
  fullscreenDontAskListeners.add(onStoreChange);
  if (!hadListeners && typeof window !== 'undefined') {
    window.addEventListener('storage', handleFullscreenDontAskStorage);
  }
  return () => {
    fullscreenDontAskListeners.delete(onStoreChange);
    if (fullscreenDontAskListeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', handleFullscreenDontAskStorage);
    }
  };
}

function getDontAskAgainSnapshot(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem(fullscreenDontAskAgain) === 'yes';
}

function getDontAskAgainServerSnapshot(): boolean {
  return false;
}

function setDontAskAgainStore(value: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(fullscreenDontAskAgain, value ? 'yes' : 'no');
    fullscreenDontAskListeners.forEach((l) => l());
  }
}

type FullscreenProviderProps = {
  children: ReactElement;
};

export const FullscreenProvider = ({ children }: FullscreenProviderProps) => {
  const analytics = useFirebaseAnalytics();
  const hasMounted = useHasMounted();
  const dontAskAgain = useSyncExternalStore(
    subscribeDontAskAgain,
    getDontAskAgainSnapshot,
    getDontAskAgainServerSnapshot,
  );

  const handle = useFullScreenHandle();

  const content = (
    <FullscreenContext.Provider
      value={{
        isFullscreen: hasMounted ? handle.active : false,
        setIsFullscreen: (state) => (state ? handle.enter() : handle.exit()),
        toggleIsFullscreen: () => (handle.active ? handle.exit() : handle.enter()),
        dontAskAgain,
        setDontAskAgain: setDontAskAgainStore,
      }}
    >
      {children}
    </FullscreenContext.Provider>
  );

  // Avoid rendering FullScreen until after mount to keep SSR and initial client render identical
  if (!hasMounted) return content;

  return (
    <FullScreen
      handle={handle}
      onChange={(isFullscreen) => {
        if (isFullscreen) {
          analytics?.logEvent('use_fullscreen_mode', { isMobile });
        } else {
          analytics?.logEvent('use_window_mode', { isMobile });
        }
      }}
    >
      {content}
    </FullScreen>
  );
};
